import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {PorQue} from '@/components/ui/por-que'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {EmployeePicker} from '@/components/employee-picker'
import {AccessResult} from '@/components/access-result'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import type {SoporteCasilla} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {tocaElAccesoDe, useGrantAccess} from '@/hooks/platform/use-access'
import {
  PERSONAS_POR_SOLICITUD,
  TOPE_NOTA_SOLICITUD,
  useCancelSolicitud,
  useCreateSolicitudes,
  type Solicitud,
  type SolicitudState,
} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {translateApiError} from '@/lib/errors'
import {formatDate, formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {cn} from '@/lib/utils'

type SolicitudFieldErrors = {note?: string; dueDate?: string}

function validateSolicitudForm(note: string, dueDate: string): SolicitudFieldErrors {
  const errors: SolicitudFieldErrors = {}
  const noteErr = validateRequired(note)
  if (noteErr) errors.note = noteErr
  const dueErr = validateRequired(dueDate)
  if (dueErr) errors.dueDate = dueErr
  return errors
}

const STATE_TONE: Record<SolicitudState, 'neutral' | 'red' | 'green'> = {
  pendiente: 'neutral',
  vencida: 'red',
  cumplida: 'green',
}

function isOpenSolicitud(solicitud: Solicitud): boolean {
  return solicitud.state !== 'cumplida'
}

export function openByEmployee(solicitudes: Solicitud[]): Map<string, Solicitud> {
  const map = new Map<string, Solicitud>()
  for (const solicitud of solicitudes) {
    if (isOpenSolicitud(solicitud)) map.set(solicitud.employee.id, solicitud)
  }
  return map
}

function defaultDueDate(): string {
  const due = new Date()
  due.setDate(due.getDate() + 15)
  return due.toISOString().slice(0, 10)
}

export function RequestSoporteDialog({
  businessId,
  standardCode,
  standardName,
  personScoped,
  roster,
  open,
  onOpenChange,
  preselected,
  soporteCasilla,
}: {
  businessId: string
  standardCode: string
  standardName: string
  /** `sgsst_standard.person_scoped`, computed server-side; never re-derived here. */
  personScoped: boolean
  roster: Employee[]
  open: boolean
  onOpenChange: (open: boolean) => void
  preselected: Employee[]
  /** The aportado file inherits this casilla (soportes esperados spec §2b). */
  soporteCasilla?: SoporteCasilla
}) {
  const {t} = useTranslation()
  const create = useCreateSolicitudes(businessId)
  const [selected, setSelected] = useState<Employee[]>([])
  const [note, setNote] = useState('')
  const [dueDate, setDueDate] = useState(defaultDueDate)
  const [fieldErrors, setFieldErrors] = useState<SolicitudFieldErrors>({})
  const [error, setError] = useState<string | null>(null)
  // Ref de callback: Radix monta el contenido después del primer efecto (ver `ui/popover.tsx`).
  const [nodoDialogo, setNodoDialogo] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (open) {
      setSelected(preselected)
      setNote('')
      setDueDate(defaultDueDate())
      setFieldErrors({})
      setError(null)
    }
  }, [open, preselected])

  const actives = roster.filter((employee) => employee.status === 'active')
  const elegidas = new Set(selected.map((employee) => employee.id))
  const aTodos = actives.length > 0 && selected.length === actives.length && actives.every((a) => elegidas.has(a.id))
  const sobran = aTodos ? 0 : selected.length - PERSONAS_POR_SOLICITUD

  function blurNote(e: FocusEvent<HTMLTextAreaElement>) {
    setFieldErrors((fe) => ({...fe, note: validateRequired(e.target.value) ?? undefined}))
  }

  function blurDueDate(e: FocusEvent<HTMLInputElement>) {
    setFieldErrors((fe) => ({...fe, dueDate: validateRequired(e.target.value) ?? undefined}))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const errors = validateSolicitudForm(note, dueDate)
    setFieldErrors(errors)
    if (errors.note || errors.dueDate) return
    create.mutate(
      {
        standard_code: standardCode,
        employee_ids: aTodos ? [] : selected.map((employee) => employee.id),
        todos_los_activos: aTodos,
        note,
        due_date: dueDate,
        soporte_key: soporteCasilla?.key ?? null,
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setNodoDialogo}>
        <DialogHeader>
          <DialogTitle>{t('solicitudes.dialog.title')}</DialogTitle>
        </DialogHeader>
        <p className="-mt-2 text-sm text-muted-foreground">
          <span className="font-mono text-primary">{standardCode}</span> · {standardName}
        </p>
        {soporteCasilla && (
          <p className="-mt-1 text-xs text-muted-foreground" data-testid="solicitud-casilla">
            <Tag tone="neutral">{soporteCasilla.titulo}</Tag>
          </p>
        )}
        {/* `PorQue` portado dentro del diálogo: el primer Escape cierra el popover, no la solicitud. */}
        <p
          className="-mt-1 flex flex-wrap items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground"
          data-testid="solicitud-motivo-hint">
          {t(personScoped ? 'solicitudes.dialog.motivoPersonScoped' : 'solicitudes.dialog.motivoTarea')}
          <PorQue
            label={t('solicitudes.dialog.motivoPorqueLabel')}
            testId="solicitud-motivo-porque"
            contenidoTestId="solicitud-motivo-porque-contenido"
            contenedor={nodoDialogo}>
            {t(personScoped ? 'solicitudes.dialog.motivoPersonScopedPorque' : 'solicitudes.dialog.motivoTareaPorque')}
          </PorQue>
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="request-soporte-form" noValidate>
          <div className="space-y-2">
            <Label>{t('solicitudes.dialog.peopleLabel')}</Label>
            <EmployeePicker roster={actives} selected={selected} onChange={setSelected} lastSelection={[]} />
            <p className="text-xs text-muted-foreground">
              {t(aTodos ? 'solicitudes.dialog.aTodos' : 'solicitudes.dialog.peopleHint')}
            </p>
            {sobran > 0 && (
              <p className="text-xs text-destructive" data-testid="solicitud-tope">
                {t('solicitudes.dialog.tope', {max: PERSONAS_POR_SOLICITUD, sobran})}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="solicitud_note">{t('solicitudes.dialog.noteLabel')}</Label>
            <Textarea
              id="solicitud_note"
              value={note}
              onChange={(event) => {
                setNote(event.target.value)
                setFieldErrors((fe) => ({...fe, note: undefined}))
              }}
              onBlur={blurNote}
              required
              maxLength={TOPE_NOTA_SOLICITUD}
              minRows={2}
              aria-invalid={!!fieldErrors.note}
            />
            <FieldError error={fieldErrors.note} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="solicitud_due">{t('solicitudes.dialog.dueLabel')}</Label>
            <Input
              id="solicitud_due"
              type="date"
              value={dueDate}
              onChange={(event) => {
                setDueDate(event.target.value)
                setFieldErrors((fe) => ({...fe, dueDate: undefined}))
              }}
              onBlur={blurDueDate}
              required
              className="max-w-44 font-mono"
              aria-invalid={!!fieldErrors.dueDate}
            />
            <FieldError error={fieldErrors.dueDate} />
          </div>
          <FormDialogFooter
            error={error}
            pending={create.isPending || selected.length === 0}
            submitDisabled={sobran > 0}
            submitLabel={t('solicitudes.dialog.submit', {count: selected.length})}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function CreateAccessAction({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const grant = useGrantAccess(businessId)
  const [error, setError] = useState<string | null>(null)

  if (grant.data) {
    return (
      <div className="w-full">
        <AccessResult grant={grant.data} />
      </div>
    )
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Tag tone="amber">{t('solicitudes.noAccount')}</Tag>
      {error ? (
        <Link
          to={`/businesses/${businessId}/employees`}
          className="text-xs font-medium text-primary hover:underline"
          data-testid="create-access-fallback">
          {t('solicitudes.createAccessFallback')}
        </Link>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[11px]"
          disabled={grant.isPending}
          data-testid="create-access"
          onClick={() => {
            setError(null)
            grant.mutate(employeeId, {onError: (err) => setError(translateApiError(err))})
          }}>
          {t('solicitudes.createAccess')}
        </Button>
      )}
    </span>
  )
}

function SolicitudDateLine({solicitud}: {solicitud: Solicitud}) {
  const {t} = useTranslation()
  const text =
    solicitud.state === 'cumplida'
      ? t('solicitudes.fulfilledOn', {date: formatDate(solicitud.fulfilled_at as string)})
      : solicitud.state === 'vencida'
        ? t('solicitudes.overdueOn', {date: formatDateOnly(solicitud.due_date)})
        : t('solicitudes.dueOn', {date: formatDateOnly(solicitud.due_date)})
  return <p className="mt-1 font-mono text-[11px] text-muted-foreground">{text}</p>
}

export function SolicitudesList({
  businessId,
  solicitudes,
  personScoped,
  editable,
}: {
  businessId: string
  solicitudes: Solicitud[]
  /** One flag for the whole list: the caller scopes `solicitudes` to a single standard. */
  personScoped: boolean
  editable: boolean
}) {
  const {t} = useTranslation()
  const cancel = useCancelSolicitud(businessId)
  const {data: business} = useBusiness(businessId)
  const soyDueno = business?.role === 'owner'
  const [cancelTarget, setCancelTarget] = useState<Solicitud | null>(null)
  if (solicitudes.length === 0) return null
  const overdue = solicitudes.filter((solicitud) => solicitud.state === 'vencida').length

  return (
    <div className="space-y-2.5" data-testid={`solicitudes-list`}>
      <div className="flex items-center gap-2.5">
        <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('solicitudes.count', {count: solicitudes.length})}
        </span>
        {overdue > 0 && <Tag tone="amber">{t('solicitudes.overdueCount', {count: overdue})}</Tag>}
        <Tag tone="neutral" data-testid="solicitudes-motivo-tag">
          {t(personScoped ? 'solicitudes.motivo.personScoped' : 'solicitudes.motivo.tarea')}
        </Tag>
      </div>
      <ul className="overflow-hidden rounded-lg border border-border">
        {solicitudes.map((solicitud) => (
          <li
            key={solicitud.id}
            data-testid="solicitud-row"
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border px-3.5 py-2.5 last:border-b-0">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="max-w-full truncate rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
                  {solicitud.employee.name} {solicitud.employee.last_name.charAt(0)}.
                </span>
                <span className="min-w-0 truncate text-xs text-muted-foreground">«{solicitud.note}»</span>
              </div>
              <SolicitudDateLine solicitud={solicitud} />
            </div>
            <Tag tone={STATE_TONE[solicitud.state]}>{t(`solicitudes.state.${solicitud.state}`)}</Tag>
            {!solicitud.employee.has_account &&
              solicitud.state !== 'cumplida' &&
              tocaElAccesoDe(solicitud.employee.role, soyDueno) && (
                <CreateAccessAction businessId={businessId} employeeId={solicitud.employee.id} />
              )}
            {editable && solicitud.state !== 'cumplida' && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] text-muted-foreground"
                data-testid="cancel-solicitud"
                onClick={() => setCancelTarget(solicitud)}>
                {t('solicitudes.cancel')}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={t('solicitudes.cancelTitle')}
        description={t('solicitudes.cancelBody', {
          name: cancelTarget ? `${cancelTarget.employee.name} ${cancelTarget.employee.last_name}` : '',
        })}
        confirmLabel={t('solicitudes.cancel')}
        cancelLabel={t('common.cancel')}
        pending={cancel.isPending}
        onConfirm={() => cancelTarget && cancel.mutate(cancelTarget.id, {onSuccess: () => setCancelTarget(null)})}
      />
    </div>
  )
}

export function CoverageRequestAction({
  solicitud,
  onRequest,
}: {
  solicitud: Solicitud | undefined
  onRequest: () => void
}) {
  const {t} = useTranslation()
  if (solicitud) {
    return (
      <span className={cn('font-mono text-[11px] text-muted-foreground')} data-testid="coverage-requested">
        {t('solicitudes.requestedUntil', {date: formatDateOnly(solicitud.due_date)})}
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={onRequest}
      data-testid="coverage-request"
      className="text-xs font-medium text-primary hover:underline">
      {t('solicitudes.request')}
    </button>
  )
}
